'use client';

import React, { forwardRef } from 'react';
import './Input.scss';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'autoComplete'> {
  label?: string | React.ReactNode;
  error?: string;
  icon?: 'user' | 'email' | 'password' | 'search' | 'phone' | 'calendar' | 'location' | 'data';
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'filled' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  helperText?: string;
  value?: string | number | readonly string[];
  autoComplete?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      icon,
      iconPosition = 'left',
      variant = 'default',
      size = 'md',
      fullWidth = false,
      helperText,
      className = '',
      type = 'text',
      disabled = false,
      required = false,
      autoComplete = false,
      ...props
    },
    ref
  ) => {
    const renderIcon = () => {
      if (!icon) return null;

      const iconMap = {
        user: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        ),
        email: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        ),
        password: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        ),
        search: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        ),
        phone: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        ),
        calendar: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        ),
        location: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        ),
        data: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <path d="M9 9h6v6H9z" />
            <path d="M9 3v6" />
            <path d="M15 3v6" />
            <path d="M9 15v6" />
            <path d="M15 15v6" />
            <path d="M3 9h6" />
            <path d="M3 15h6" />
            <path d="M15 9h6" />
            <path d="M15 15h6" />
          </svg>
        ),
      };

      const iconClasses = [
        'input__icon',
        `input__icon--${iconPosition}`,
        // Ajouter une classe spécifique pour l'icône calendar ou si le type est date
        (icon === 'calendar' || type === 'date') && 'input__icon--data'
      ].filter(Boolean).join(' ');

      return <span className={iconClasses}>{iconMap[icon]}</span>;
    };

    // Fonction pour empêcher la saisie de lettres dans les champs numériques
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Si le type est number, empêcher la saisie de lettres
      if (type === 'number') {
        const key = e.key;
        
        // Autoriser les touches de contrôle (Backspace, Delete, Tab, Escape, Enter, arrows)
        if ([
          'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
          'Home', 'End'
        ].includes(key)) {
          return;
        }
        
        // Autoriser Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
        if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase())) {
          return;
        }
        
        // Autoriser les chiffres (0-9)
        if (key >= '0' && key <= '9') {
          return;
        }
        
        // Autoriser le point décimal (une seule fois)
        if (key === '.' && !e.currentTarget.value.includes('.')) {
          return;
        }
        
        // Autoriser le signe moins seulement au début
        if (key === '-' && e.currentTarget.selectionStart === 0 && !e.currentTarget.value.includes('-')) {
          return;
        }
        
        // Empêcher toutes les autres touches (lettres, symboles)
        e.preventDefault();
      }
      
      // Appeler le gestionnaire onKeyDown personnalisé s'il existe
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    const inputClasses = [
      'input',
      `input--${variant}`,
      `input--${size}`,
      icon && `input--has-icon-${iconPosition}`,
      fullWidth && 'input--full-width',
      error && 'input--error',
      disabled && 'input--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="input-wrapper">
        {label && (
          <label className="input__label" htmlFor={props.id}>
            {label}
            {required && <span className="input__required">*</span>}
          </label>
        )}
        
        <div className="input__container">
          {icon && iconPosition === 'left' && renderIcon()}
          <input
            ref={ref}
            type={type}
            className={inputClasses}
            disabled={disabled}
            required={required}
            onKeyDown={handleKeyDown}
            aria-invalid={!!error}
            autoComplete={autoComplete ? 'on' : 'off'}
            aria-describedby={props.id ? `${props.id}-error` : undefined}
            {...props}
          />
          {icon && iconPosition === 'right' && renderIcon()}
        </div>

        {(error || helperText) && (
          <div
            className={`input__helper-text ${error ? 'input__helper-text--error' : ''}`}
            id={props.id ? `${props.id}-error` : undefined}
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;